import type { ReactElement } from 'react';
import type { DocumentSectionType } from '@shared/types';
import { PageShell } from '../Page';
import type { PageProps } from '../types';
import { EditableText } from '../../components/Editable';
import { ImageSlot } from '../../components/ImageSlot';
import { visibleSections } from '../../lib/plan';
import { OVERVIEW_DEFAULT_IMAGE, resolveHeroImage } from './assets';
import { Frame, HairlineList, NumberedLabel, focalPointStyle } from './primitives';

const DEFAULT_HEADLINE = 'A clear path forward.';

/**
 * The five-act narrative (reference/proposal-template-brief-v2.md §1) as a
 * type -> act map, defined here (not shared) per design.md V15's slide-map
 * note that the agenda derives its items from "the visible section types
 * via a type -> act map defined in the slide". Every `DocumentSectionType`
 * that can appear in a momentum_v3 deck is covered exactly once.
 */
const ACTS: { label: string; types: DocumentSectionType[] }[] = [
  { label: 'Their world', types: ['cover', 'agenda', 'overview', 'stats'] },
  {
    label: 'The idea',
    types: ['strategy', 'customer_journey', 'campaign_architecture', 'audience_profiles', 'geo_strategy'],
  },
  {
    label: 'The plan',
    types: [
      'media_mix',
      'product_divider',
      'product_education',
      'product_plan',
      'media_product',
      'tactic_audience',
      'tactic_moment',
      'tactic_measurement',
      'tactic_group',
      'monthly_schedule',
    ],
  },
  { label: 'What you get', types: ['projections', 'reporting', 'why_us'] },
  { label: 'The ask', types: ['budget_summary', 'terms', 'thank_you'] },
];

/** Which of the five acts have at least one visible section in this deck, in fixed act order (never reordered/authored). */
function actsPresent(proposal: PageProps['proposal']): string[] {
  const present = new Set(visibleSections(proposal).map((section) => section.type));
  return ACTS.filter((act) => act.types.some((type) => present.has(type))).map((act) => act.label);
}

/**
 * agenda (brief §4 `02`): SPLIT 60/40 dark, optional (design.md V15 skeleton:
 * emitted only when `document.options.showAgenda`). Eyebrow + h1 (default "A
 * clear path forward.") + mint rule + a short intro over a HairlineList of
 * NumberedLabel act names, and a 40%-width photo slot running to the card
 * edge. Items are fully derived (§5/§6: "*derived:* items = act names
 * present in the deck"), never authored.
 */
export function AgendaPage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const acts = actsPresent(proposal);
  const imageUrl = section.content.imageUrl;
  const heroSrc = resolveHeroImage({ imageUrl, fallback: OVERVIEW_DEFAULT_IMAGE });

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv3-agenda-root"
      hideTitle
    >
      <Frame surface="dark" proposal={proposal} section={section}>
        <div className="mv3-agenda-grid">
          <div className="mv3-agenda-left">
            <div className="mv3-agenda-intro">
              <span className="mv3-eyebrow">Agenda</span>
              <div className="mv3-agenda-headline">
                <EditableText
                  value={section.content.headline || DEFAULT_HEADLINE}
                  field="headline"
                  placeholder={DEFAULT_HEADLINE}
                  readOnly={readOnly}
                  variant="headline"
                  onSave={(headline) => actions.saveContent(section, { headline })}
                />
              </div>
              <div className="mv3-rule" aria-hidden="true" />
              <div className="mv3-agenda-body">
                <EditableText
                  value={section.content.body}
                  field="body"
                  placeholder="Add a short framing line"
                  readOnly={readOnly}
                  onSave={(body) => actions.saveContent(section, { body })}
                />
              </div>
            </div>
            <div className="mv3-agenda-list">
              <HairlineList
                items={acts.map((label, index) => (
                  <NumberedLabel key={label} number={String(index + 1).padStart(2, '0')} label={label} inverse />
                ))}
                className="mv3-agenda-acts"
              />
            </div>
          </div>
          <div className="mv3-agenda-hero">
            <img src={heroSrc} alt="Agenda" className="mv3-agenda-image" style={focalPointStyle(section)} />
            {!readOnly ? (
              <ImageSlot
                url={imageUrl}
                alt="Agenda"
                readOnly={readOnly}
                onSave={(next) => actions.saveContent(section, { imageUrl: next })}
                className="mv3-agenda-image-affordance"
                hideImage
                proposalId={proposal.proposalId}
                defaultQuery={proposal.advertiser.industry ?? section.title}
              />
            ) : null}
          </div>
        </div>
      </Frame>
    </PageShell>
  );
}
